import { useEffect, useState } from 'react'

/*
 * Live spot price for crypto price up/down cards. One Binance combined stream serves every
 * subscriber on the page: a pair is added on its first subscriber and dropped on its last,
 * and the socket closes once nobody is listening. `miniTicker` ticks once a second per pair,
 * which is as often as a feed card should repaint. A REST read fills the first value so a
 * card is not blank while the socket connects. The detail page keeps its own candlestick
 * stream; this is for lists only.
 *
 * Binance accepts at most 5 incoming messages a second and drops the socket past that, so
 * subscribe/unsubscribe requests made in the same tick (a page of cards mounting) go out as
 * one frame. Reconnects back off exponentially: when the stream is unreachable (it is
 * geo-blocked in some regions) the page must not hammer it every few seconds.
 */

const STREAM_URL = 'wss://stream.binance.com:9443/stream'
const PRICE_URL = 'https://api.binance.com/api/v3/ticker/price'
const RECONNECT_BASE_MS = 3000
const RECONNECT_MAX_MS = 5 * 60 * 1000

const subscribers = new Map()
const prices = new Map()
const pendingSubscribe = new Set()
const pendingUnsubscribe = new Set()
let socket = null
let reconnectTimer = null
let reconnectAttempt = 0
let flushTimer = null
let messageId = 1

const streamName = symbol => `${symbol}@miniTicker`

const emit = symbol => {
  const price = prices.get(symbol)
  subscribers.get(symbol)?.forEach(listener => listener(price))
}

const send = (method, params) => {
  if (socket?.readyState === 1 && params.length) {
    socket.send(JSON.stringify({ method, params, id: messageId++ }))
  }
}

/* Sends every queued stream change in one SUBSCRIBE and one UNSUBSCRIBE frame. */
const flushPending = () => {
  flushTimer = null
  if (socket?.readyState !== 1) return
  send('SUBSCRIBE', [...pendingSubscribe].map(streamName))
  send('UNSUBSCRIBE', [...pendingUnsubscribe].map(streamName))
  pendingSubscribe.clear()
  pendingUnsubscribe.clear()
}

const scheduleFlush = () => {
  if (flushTimer === null) flushTimer = setTimeout(flushPending, 0)
}

const queue = (symbol, isSubscribe) => {
  const add = isSubscribe ? pendingSubscribe : pendingUnsubscribe
  const remove = isSubscribe ? pendingUnsubscribe : pendingSubscribe
  // A pair dropped and re-added in the same tick needs no message at all.
  if (remove.delete(symbol)) return
  add.add(symbol)
  scheduleFlush()
}

const fetchInitialPrice = async symbol => {
  try {
    const response = await fetch(`${PRICE_URL}?symbol=${encodeURIComponent(symbol.toUpperCase())}`)
    if (!response.ok) return
    const body = await response.json()
    const price = Number(body?.price)
    if (Number.isFinite(price) && !prices.has(symbol) && subscribers.has(symbol)) {
      prices.set(symbol, price)
      emit(symbol)
    }
  } catch {
    // The stream delivers the price a moment later; nothing to recover here.
  }
}

const openSocket = () => {
  reconnectTimer = null
  if (socket || typeof WebSocket === 'undefined') return
  const ws = new WebSocket(STREAM_URL)
  socket = ws

  ws.onopen = () => {
    // The open frame carries every pair in one go; anything queued before it is covered.
    pendingSubscribe.clear()
    pendingUnsubscribe.clear()
    send('SUBSCRIBE', [...subscribers.keys()].map(streamName))
  }

  ws.onmessage = event => {
    let message
    try {
      message = JSON.parse(event.data)
    } catch {
      return
    }
    const ticker = message?.data
    if (ticker?.e !== '24hrMiniTicker') return
    reconnectAttempt = 0
    const symbol = String(ticker.s).toLowerCase()
    const price = Number(ticker.c)
    if (!Number.isFinite(price)) return
    prices.set(symbol, price)
    emit(symbol)
  }

  ws.onerror = () => ws.close()

  ws.onclose = () => {
    if (socket !== ws) return
    socket = null
    if (!subscribers.size) return
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempt, RECONNECT_MAX_MS)
    reconnectAttempt += 1
    reconnectTimer = setTimeout(openSocket, delay)
  }
}

const closeSocket = () => {
  clearTimeout(reconnectTimer)
  reconnectTimer = null
  clearTimeout(flushTimer)
  flushTimer = null
  pendingSubscribe.clear()
  pendingUnsubscribe.clear()
  reconnectAttempt = 0
  const ws = socket
  socket = null
  ws?.close()
}

const subscribe = (symbol, listener) => {
  let listeners = subscribers.get(symbol)
  if (!listeners) {
    listeners = new Set()
    subscribers.set(symbol, listeners)
    fetchInitialPrice(symbol)
    queue(symbol, true)
    openSocket()
  }
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
    if (listeners.size) return
    subscribers.delete(symbol)
    // The last price goes with the last listener: a later mount must not paint it as live.
    prices.delete(symbol)
    if (subscribers.size) queue(symbol, false)
    else closeSocket()
  }
}

/**
 * Latest price of a Binance pair (lowercase symbol, e.g. `btcusdt`), or null until the first
 * tick. Pass null to subscribe to nothing.
 */
const useCryptoLivePrice = symbol => {
  const [price, setPrice] = useState(() => (symbol ? prices.get(symbol) ?? null : null))

  useEffect(() => {
    if (!symbol) {
      setPrice(null)
      return undefined
    }
    setPrice(prices.get(symbol) ?? null)
    return subscribe(symbol, setPrice)
  }, [symbol])

  return price
}

export default useCryptoLivePrice
